import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { entityService } from '@/services/EntityService';
import { AssetService } from '@/services/AssetService';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { logger } from '@equip/utils/logger';
import { useHierarchyEntities } from '@/hooks';
import { buildLocationLabel, filterLeafLocationOptions } from '@/utils/locationHierarchy';
import type { AssetInventoryGroup } from '@/hooks/useAssetInventory';
import type { Entity } from '@/types/Entity';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { buildParentIdSet } from '@/utils/catalogUtils';

const log = logger.child({ component: 'AdjustAssetTypeDialog' });

interface AdjustAssetTypeDialogProps {
  open: boolean;
  group: AssetInventoryGroup | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface CatalogOption {
  id: string;
  label: string;
  entityIdentifier: string;
  lin: string;
  nsn: string;
}

interface LocationOption {
  id: string;
  label: string;
  breadcrumb: string | null;
  identifier: string | null;
  group: string;
}

const GROUP_KNOWN = 'Known Locations';
const GROUP_ALL = 'All Locations';

const isStockCatalog = (entityData: Record<string, unknown> | null | undefined): boolean => {
  const typeValue = entityData?.type;
  return typeof typeValue === 'string' && typeValue.toLowerCase() === 'stock';
};

const buildCatalogOption = (entity: Entity): CatalogOption => ({
  id: entity.id,
  label: entity.display_name || 'Unnamed Catalog',
  entityIdentifier: entity.entity_identifier || '',
  lin: typeof entity.entity_data?.lin === 'string' ? entity.entity_data.lin : '—',
  nsn: typeof entity.entity_data?.nsn === 'string' ? entity.entity_data.nsn : '—',
});

export const AdjustAssetTypeDialog: React.FC<AdjustAssetTypeDialogProps> = ({
  open,
  group,
  onClose,
  onSuccess,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [targetCatalog, setTargetCatalog] = useState<CatalogOption | null>(null);
  const [destinationLocation, setDestinationLocation] = useState<LocationOption | null>(null);
  const [justification, setJustification] = useState('');
  const [catalogs, setCatalogs] = useState<CatalogOption[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferredLocationIds, setPreferredLocationIds] = useState<string[]>([]);

  const maxQuantity = group?.stock_quantity ?? 0;

  const locationQuery = useHierarchyEntities({
    dataSource: {
      endpoint: '/api/rest/data/v1/location_hierarchy',
      order: 'breadcrumb.asc',
    },
    filters: {
      select: 'id,display_name,entity_identifier,breadcrumb,node_type_name',
    },
    enabled: open,
  });

  // Fetch preferred locations when dialog opens or when targetCatalog changes
  useEffect(() => {
    if (open && targetCatalog) {
      AssetService.getPreferredLocations(targetCatalog.id)
        .then((ids) => {
          setPreferredLocationIds(ids);
          log.info('Loaded preferred locations for adjust type', {
            catalogId: targetCatalog.id,
            count: ids.length,
          });
        })
        .catch((err) => {
          log.warn('Failed to load preferred locations for adjust type', { error: String(err) });
          setPreferredLocationIds([]);
        });
    } else if (open) {
      setPreferredLocationIds([]);
    }
  }, [open, targetCatalog]);

  const fetchCatalogs = useCallback(
    async (query = '') => {
      setLoadingCatalogs(true);
      try {
        const entities = await entityService.searchStockCatalogs(query);

        const parentIds = buildParentIdSet(entities);
        const mapped = entities
          .filter((entity) => isStockCatalog(entity.entity_data))
          .filter((entity) => entity.id !== group?.catalog_id)
          .filter((entity) => !parentIds.has(entity.id))
          .map(buildCatalogOption);

        setCatalogs(mapped);
      } catch (err) {
        log.error('Failed to fetch catalog options for adjust type', { error: String(err) });
      } finally {
        setLoadingCatalogs(false);
      }
    },
    [group?.catalog_id],
  );

  useEffect(() => {
    if (!open || !group) return;

    setQuantity(Math.max(1, group.stock_quantity));
    setTargetCatalog(null);
    setDestinationLocation(null);
    setJustification('');
    setError(null);
    void fetchCatalogs();
  }, [open, group, fetchCatalogs]);

  // Build sorted location options with group assignments (preferred first)
  const locationOptions = useMemo<LocationOption[]>(() => {
    const allOptions = (locationQuery.data ?? []).map((row) => ({
      id: row.id,
      label: buildLocationLabel(row),
      breadcrumb: row.breadcrumb ?? null,
      identifier: row.entity_identifier ?? null,
      group: preferredLocationIds.includes(row.id) ? GROUP_KNOWN : GROUP_ALL,
    }));

    const leafOptions = filterLeafLocationOptions(allOptions);

    // Sort: known locations first, then alphabetical by breadcrumb
    return leafOptions.sort((a, b) => {
      if (a.group === GROUP_KNOWN && b.group !== GROUP_KNOWN) return -1;
      if (a.group !== GROUP_KNOWN && b.group === GROUP_KNOWN) return 1;
      return (a.breadcrumb ?? a.label).localeCompare(b.breadcrumb ?? b.label);
    });
  }, [locationQuery.data, preferredLocationIds]);

  const filterOptions = useMemo(
    () =>
      createFilterOptions<LocationOption>({
        stringify: (option) =>
          [option.label, option.breadcrumb ?? '', option.identifier ?? ''].join(' '),
      }),
    [],
  );

  // Clear destination location when targetCatalog changes
  useEffect(() => {
    setDestinationLocation(null);
  }, [targetCatalog]);

  const handleSubmit = async () => {
    if (!group || !targetCatalog || !destinationLocation || quantity < 1) {
      return;
    }

    setSubmitting(true);
    setError(null);
    const da4949Window = preOpenWindow();
    try {
      const result = await AssetService.adjustAssetType({
        assetIds: group.asset_ids,
        quantity,
        targetCatalogId: targetCatalog.id,
        destinationLocationId: destinationLocation.id,
        justification: justification.trim() || undefined,
      });

      if (result.da4949GeneratedDocumentId) {
        await openDocumentWithAuth(
          `/api/rest/app/v1/documents/${result.da4949GeneratedDocumentId}`,
          da4949Window,
        );
      } else if (da4949Window && !da4949Window.closed) {
        da4949Window.close();
      }
      onSuccess();
      onClose();
    } catch (err) {
      if (da4949Window && !da4949Window.closed) {
        da4949Window.close();
      }
      const message = getErrorMessage(err);
      log.error('Adjust asset type failed', { error: String(err) });
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adjust Type</DialogTitle>
      <DialogContent>
        {!group ? null : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Convert a selected quantity from this available stock row into a new catalog type at a
              destination location.
            </Typography>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Source
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {group.asset_name} ({group.lin || '—'} / {group.nsn || '—'})
              </Typography>
            </Box>

            <Box>
              <Typography gutterBottom>Quantity</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Slider
                  value={quantity}
                  onChange={(_, value) => setQuantity(value as number)}
                  min={1}
                  max={Math.max(1, maxQuantity)}
                  valueLabelDisplay="auto"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  value={quantity}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (!Number.isFinite(next)) return;
                    if (next < 1 || next > maxQuantity) return;
                    setQuantity(next);
                  }}
                  inputProps={{ min: 1, max: maxQuantity }}
                  size="small"
                  sx={{ width: 88 }}
                />
              </Stack>
            </Box>

            <Autocomplete
              options={catalogs}
              loading={loadingCatalogs}
              value={targetCatalog}
              onChange={(_event, value) => setTargetCatalog(value)}
              onInputChange={(_event, value) => {
                void fetchCatalogs(value);
              }}
              getOptionLabel={(option) => `${option.label} (${option.entityIdentifier})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options) => options}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box component="li" key={key} {...optionProps}>
                    <Stack spacing={0.3}>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.entityIdentifier} • LIN {option.lin} • NSN {option.nsn}
                      </Typography>
                    </Stack>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New Type"
                  placeholder="Search catalog by name or identifier"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCatalogs ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              options={locationOptions}
              loading={locationQuery.isLoading}
              value={destinationLocation}
              onChange={(_event, value) => setDestinationLocation(value)}
              getOptionLabel={(option) => (option.breadcrumb ? option.breadcrumb : option.label)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              groupBy={(option) => option.group}
              filterOptions={filterOptions}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.breadcrumb && (
                      <Typography variant="caption" color="text.secondary">
                        {option.breadcrumb}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination Location"
                  placeholder="Search by name, path, or ID"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {locationQuery.isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Justification"
              placeholder="Enter justification for DA 4949"
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              disabled={submitting}
            />

            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <ViewerGuardedButton
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!group || !targetCatalog || !destinationLocation || quantity < 1 || submitting}
        >
          {submitting ? 'Updating…' : 'Update'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
